import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { closePaywall, openPaywall } from "@/shared/lib/paywall";
import { usePaywallReason } from "@/shared/lib/hooks/usePaywall";
import { track } from "@/shared/lib/analytics";
import { Spinner, Text } from "@/shared/ui";
import { useCheckout } from "../hooks/useCheckout";
import { useSubscription } from "../hooks/useSubscription";
import { displayPrice, formatUsd, PLANS } from "../lib/plans";
import { getPaywallVariant, preloadPaywallMedia } from "../lib/paywallVariants";
import { PaywallMasonry } from "./PaywallMasonry";

/** Per-layout scrim class (gradients + red wash live in globals.css `.pw-scrim-*`). */
const SCRIM_CLASS: Record<string, string> = {
  immersive: "pw-scrim-immersive",
  bottom: "pw-scrim-bottom",
  split: "pw-scrim-split",
};

/** Headline scale per layout — immersive shouts, split is compact beside its panel. */
const HEADLINE_CLASS: Record<string, string> = {
  immersive: "text-[clamp(34px,5.4vw,60px)]",
  bottom: "text-[clamp(32px,5vw,52px)]",
  split: "text-[clamp(30px,4.4vw,48px)]",
};

/**
 * The model paywall, run as a four-way A/B (see `paywallVariants`). Each visitor
 * is bucketed once into a treatment built from a moving masonry of generated
 * media — images or autoplaying clips, immersive / lower-band / split — and the
 * assigned variant is attached to the Polar checkout so we can read a conversion
 * delta per treatment. A locked model opens this; the CTA starts checkout for the
 * required plan, "See all plans" routes to `/pricing`. Mounted once at app root.
 */
export function PaywallHost() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reason = usePaywallReason();
  const checkout = useCheckout();
  const { data: subscription } = useSubscription();
  const checkoutEnabled = subscription?.capabilities?.web_checkout_enabled ?? false;
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [variant] = useState(getPaywallVariant);

  const open = reason !== null;

  // Warm the assigned variant's media once on mount, so the first open is instant.
  useEffect(() => {
    preloadPaywallMedia();
  }, []);

  // DEV-only: `?pwv=a|b|c|d` auto-opens the paywall so every treatment can be
  // previewed straight from the URL — no sign-in, no dev menu. Tree-shaken out
  // of production by the `DEV` guard.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    try {
      const q = new URLSearchParams(window.location.search).get("pwv");
      const preview = q ?? sessionStorage.getItem("aa.pwv");
      if (preview) openPaywall({ kind: "model", modelName: "GPT-5.5", plan: "creator" });
    } catch {
      // ignore — preview convenience only
    }
  }, []);

  // A/B: record an impression per open, tagged with the assigned treatment.
  useEffect(() => {
    if (!reason) return;
    track("paywall_viewed", {
      variant: variant.id,
      layout: variant.layout,
      kind: variant.kind,
      plan: reason.plan,
      model: reason.modelName,
    });
  }, [reason, variant]);

  useEffect(() => {
    if (!open) return;
    primaryRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") closePaywall();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!reason) return null;

  const plan = PLANS.find((p) => p.id === reason.plan);
  const planName = t(`pricing.plans.${reason.plan}.name`);
  const priceLabel = plan
    ? t("paywall.perMonth", { price: formatUsd(displayPrice(plan.pricing, "month").amount) })
    : "";

  function startCheckout(): void {
    if (!reason) return;
    if (!checkoutEnabled) {
      closePaywall();
      void navigate({ to: "/pricing" });
      return;
    }
    track("paywall_upgrade_clicked", {
      variant: variant.id,
      layout: variant.layout,
      plan: reason.plan,
    });
    checkout.mutate(
      { plan: reason.plan, interval: "month", variant: variant.id },
      { onSuccess: ({ url }) => (window.location.href = url) },
    );
  }

  const split = variant.layout === "split";

  const content = (
    <>
      <span className="pw-badge inline-flex items-center rounded-pill px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-white">
        {planName}
      </span>

      <Text
        variant="display"
        as="h2"
        id="paywall-title"
        className={`${HEADLINE_CLASS[variant.layout]} leading-[1.03] text-white`}
      >
        {t(variant.headlineKey)}
      </Text>

      <Text variant="body" className="max-w-md text-[15px] text-white/75">
        {t("paywall.description", { model: reason.modelName, plan: planName })}
      </Text>

      {checkout.isError ? (
        <Text variant="body" as="p" className="text-[13px] text-white/80">
          {t("paywall.checkoutError")}
        </Text>
      ) : null}

      <div className="mt-1 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <button
          ref={primaryRef}
          type="button"
          disabled={checkout.isPending}
          onClick={startCheckout}
          className="pw-cta inline-flex h-12 items-center justify-center gap-2 rounded-pill px-7 font-sans text-[15px] font-semibold text-white outline-none transition-[filter,transform] duration-150 ease-house active:scale-[0.99] disabled:opacity-60"
        >
          {!checkoutEnabled ? (
            t("pricing.checkoutUnavailable")
          ) : checkout.isPending ? (
            <Spinner size={16} />
          ) : (
            <>
              {t("paywall.upgradeTo", { plan: planName })}
              {priceLabel ? <span className="text-white/70">· {priceLabel}</span> : null}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            closePaywall();
            void navigate({ to: "/pricing" });
          }}
          className="inline-flex h-12 items-center justify-center rounded-pill border border-white/20 bg-white/[0.06] px-6 font-sans text-[15px] font-medium text-white/90 outline-none backdrop-blur-sm transition-colors duration-150 ease-house hover:border-white/40 hover:text-white focus-visible:ring-[3px] focus-visible:ring-white/30"
        >
          {t("paywall.seeAllPlans")}
        </button>
      </div>
    </>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
    >
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={closePaywall}
        className="absolute inset-0 cursor-default bg-black/75 backdrop-blur-sm"
      />

      <div className="relative flex size-full max-w-[1240px] overflow-hidden rounded-[28px] bg-[var(--pw-ink)] shadow-pill">
        {/* Moving masonry — full-bleed, except the lower-band layout keeps it up top. */}
        {variant.layout === "bottom" ? (
          <div className="absolute inset-x-0 top-0 h-[58%]">
            <PaywallMasonry variant={variant} />
          </div>
        ) : split ? (
          <div className="absolute inset-y-0 right-0 w-[60%]">
            <PaywallMasonry variant={variant} />
          </div>
        ) : (
          <PaywallMasonry variant={variant} />
        )}

        <div className={`absolute inset-0 ${SCRIM_CLASS[variant.layout]}`} />

        <button
          type="button"
          onClick={closePaywall}
          aria-label={t("common.close")}
          className="absolute right-4 top-4 z-20 grid size-10 place-items-center rounded-full bg-black/40 text-white outline-none backdrop-blur-sm transition-colors duration-150 ease-house hover:bg-black/65 focus-visible:ring-[3px] focus-visible:ring-white/40 [&_svg]:size-5"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {split ? (
          <div className="relative z-10 flex w-[44%] min-w-[300px] flex-col items-start justify-center gap-5 p-7 sm:p-12">
            {content}
          </div>
        ) : (
          <div className="relative z-10 mt-auto flex w-full max-w-[640px] flex-col items-start gap-5 p-7 sm:p-12">
            {content}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
